import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Player } from "@lottiefiles/react-lottie-player";
import { useIntl } from "react-intl";

import className from "../style.module.scss";

export function AnimationEntity(props) {
  const intl = useIntl(); // current language = intl.locale
  const player = React.createRef();
  const [triggered, setTriggered] = useState(false);
  const hasReadiedRef = React.useRef(false);

  // Check if playOnce is defined; default to false if undefined
  const playOnce = props.data.playOnce ?? false;

  // Screen-reader status announcements for this animation, delegated to the
  // parent's consolidated live region via props.onAnnounce (the entity itself
  // is aria-hidden). Gated by `ariaLive` !== "none"; the message comes from
  // `startAnnouncement` (when the animation begins playing) and
  // `completeAnnouncement` (when it finishes) — e.g. the choice-chamber timer
  // announces "The timer has started." then "The timer has ended…".
  const hasAnnouncedStartRef = React.useRef(false);
  const hasAnnouncedCompleteRef = React.useRef(false);
  const announceEnabled =
    !!props.data.ariaLive && props.data.ariaLive !== "none";
  // Whether this animation speaks its own completion status. When true, the
  // parent must NOT also announce the control this animation reveals (that
  // generic "control revealed" status would duplicate/override this one).
  // `onAnnounce` is part of the condition because announceLifecycle refuses to
  // speak without it: claiming to self-announce while unable to would suppress
  // the parent's generic status too and leave the user with silence.
  const selfAnnouncesComplete =
    announceEnabled && !!props.onAnnounce && !!props.data.completeAnnouncement;

  const announceLifecycle = (phase) => {
    if (!announceEnabled || !props.onAnnounce) return;
    if (phase === "start") {
      if (hasAnnouncedStartRef.current || !props.data.startAnnouncement) return;
      hasAnnouncedStartRef.current = true;
      props.onAnnounce(props.data.startAnnouncement);
    } else if (phase === "complete") {
      if (hasAnnouncedCompleteRef.current || !props.data.completeAnnouncement) {
        return;
      }
      hasAnnouncedCompleteRef.current = true;
      props.onAnnounce(props.data.completeAnnouncement);
    }
  };

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  useEffect(() => {
    // Only undo the trigger on mount if the animation hasn't already played.
    // If it has played (state is in playedStates), the trigger is legitimately
    // persisted and dependent hotspots should remain visible.
    if (!props.playedStates.has(props.state)) {
      props.undoTrigger(props.data.customTrigger);
    }
  }, []);

  // One AnimationEntity instance serves every state of an entity — the parent
  // passes `data={entity.states[state]}`, so startAnnouncement/completeAnnouncement
  // change with `props.state` while the latch refs live for the component's
  // lifetime. Without this reset only the FIRST announcing state would ever be
  // heard. Declared above the initiatePlay effect so it runs first on a state
  // change, re-arming the latches before the new state plays.
  useEffect(() => {
    hasAnnouncedStartRef.current = false;
    hasAnnouncedCompleteRef.current = false;
  }, [props.state]);

  useEffect(() => {
    if (!props.playedStates.has(props.state)) {
      initiatePlay(); // Only play if state hasn't been played before
    }
  }, [props.state, props.hidden]);

  // Handle pause/resume for looping animations only
  useEffect(() => {
    if (!props.data.loop) {
      return;
    }

    // Skip static animations (start === end)
    if (props.data.frameRange.start === props.data.frameRange.end) {
      return;
    }

    if (props.pauseLooping && !props.hidden) {
      player.current?.pause();
    } else if (!props.pauseLooping && !props.hidden) {
      // Only resume if animation should be playing (within frame range)
      const currentFrame = player.current?.state?.instance?.currentFrame;
      if (
        currentFrame !== undefined &&
        currentFrame >= props.data.frameRange.start &&
        currentFrame <= props.data.frameRange.end
      ) {
        player.current?.play();
      }
    }
  }, [
    props.pauseLooping,
    props.hidden,
    props.data.loop,
    props.data.frameRange,
  ]);

  const initiatePlay = () => {
    if (!props.hidden) {
      player.current?.stop();
      if (props.data.frameRange.start !== props.data.frameRange.end) {
        player.current.setPlayerSpeed(props.data.speed);
        player.current.setSeeker(props.data.frameRange.start, true);

        // Only play if:
        // 1. Animation is NOT looping, OR
        // 2. Animation IS looping but pause is NOT active
        const shouldPlay = !props.data.loop || !props.pauseLooping;

        if (shouldPlay) {
          player.current.play();
          announceLifecycle("start");
        }
      } else {
        player.current.setSeeker(props.data.frameRange.start, false);
      }
    }
  };

  const isLoaded = () => {
    if (!props.playedStates.has(props.state)) {
      initiatePlay();
    }
  };

  const newFrame = () => {
    if (
      Math.ceil(player.current.state.instance.currentFrame) ===
      props.data.frameRange.end
    ) {
      if (props.data.loop) {
        player.current.setSeeker(props.data.frameRange.start, true);
      } else {
        player.current.pause();
      }

      // Mark the state as played at the end frame only if playOnce is true
      if (playOnce && !props.playedStates.has(props.state)) {
        if (props.onStateMarkedPlayed) {
          props.onStateMarkedPlayed(props.state);
        } else {
          props.playedStates.add(props.state);
        }
      }

      if (props.data.customTrigger !== "" && !triggered) {
        setTriggered(true);
        props.fireTrigger(props.data.customTrigger, selfAnnouncesComplete);
      }
      announceLifecycle("complete");
    }
  };

  const handleEnd = () => {
    if (props.data.loop) {
      player.current.setSeeker(props.data.frameRange.start, true);
      player.current.play();
    }

    // Mark the state as played on complete only if playOnce is true
    if (playOnce && !props.playedStates.has(props.state)) {
      if (props.onStateMarkedPlayed) {
        props.onStateMarkedPlayed(props.state);
      } else {
        props.playedStates.add(props.state);
      }
    }

    if (props.data.customTrigger !== "" && !triggered) {
      setTriggered(true);
      props.fireTrigger(props.data.customTrigger, selfAnnouncesComplete);
    }
    announceLifecycle("complete");
  };

  const [src, setSrc] = useState(
    props.preview
      ? props.previewSrc
      : `${baseUrl}locales/${intl.locale}/animations/${props.src}`
  );

  const loadError = () => {
    props.preview
      ? props.previewSrc
      : setSrc(`${baseUrl}locales/en/animations/${props.src}`);
  };

  useEffect(() => {
    props.preview
      ? props.previewSrc
      : setSrc(`${baseUrl}locales/${intl.locale}/animations/${props.src}`);
  }, [intl.locale]);

  // Prefer caller-supplied parsed JSON when available; fall back to URL.
  const playerSrc = props.animationData ?? src;

  return (
    <div
      className={className.entityContainer}
      style={{
        overflow: !props.allowOverflow ? "hidden" : null,
        display: props.hidden ? "none" : null,
      }}
      aria-hidden={true}
      role="presentation"
    >
      <div className={className.animationEntityContainer} aria-hidden>
        <Player
          key={src} // forces remount when locale URL changes
          ref={player}
          src={playerSrc}
          onEvent={(event) => {
            if (event === "error") loadError();
            if (event === "load") {
              isLoaded();
              if (!hasReadiedRef.current) {
                hasReadiedRef.current = true;
                props.onReady?.();
              }
            }
            if (event === "frame") newFrame();
            if (event === "complete") handleEnd();
          }}
          keepLastFrame
          preserveAspectRatio="xMaxYMid meet"
          style={{
            width: "100%",
          }}
        ></Player>
      </div>
    </div>
  );
}

AnimationEntity.propTypes = {
  data: PropTypes.shape({
    frameRange: PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }).isRequired,
    speed: PropTypes.number.isRequired,
    loop: PropTypes.bool.isRequired,
    altText: PropTypes.string.isRequired,
    playOnce: PropTypes.bool, // Optional playOnce property
    // Enables/disables this animation's status announcements. Any value other
    // than "none" turns them on; the severity itself is NOT forwarded, because
    // the parent delivers every announcement through a single polite region.
    // Honouring "assertive" would need a separate assertive region — follow-up
    // work, tracked with the announcer rework.
    ariaLive: PropTypes.oneOf(["none", "polite", "assertive"]),
    // Optional screen-reader status messages announced (via onAnnounce) when the
    // animation starts playing / finishes, when ariaLive !== "none".
    startAnnouncement: PropTypes.string,
    completeAnnouncement: PropTypes.string,
  }).isRequired,
  src: PropTypes.string.isRequired,
  allowOverflow: PropTypes.bool.isRequired,
  state: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
  playedStates: PropTypes.object.isRequired, // Passed from parent
  onStateMarkedPlayed: PropTypes.func, // Optional callback when a state is marked as played
  hidden: PropTypes.bool,
  preview: PropTypes.bool,
  previewSrc: PropTypes.string,
  undoTrigger: PropTypes.func,
  fireTrigger: PropTypes.func,
  // Called with (message) to announce this animation's start/complete status
  // via the parent's consolidated live region.
  onAnnounce: PropTypes.func,
  pauseLooping: PropTypes.bool, // Control for pausing looping animations
  animationData: PropTypes.object, // Optional pre-fetched Lottie JSON; overrides URL src
  onReady: PropTypes.func, // Optional callback fired once on the lottie 'load' event
};
